// src/app/features/questionnaires/checkout/index.tsx
import PaymentCards from 'app/features/payments/PaymentCards';
import CartItemCard from 'app/features/services/cartItemCard';
import {
  DashboardContainerHeader,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import { useGetCartItemsQuery } from 'app/services/payments/cart';
import {
  useCheckoutOrderMutation,
  useCreateOrderMutation,
} from 'app/services/payments/orders';
import { useGetProjectByIdQuery } from 'app/services/projects';
import { IPromoCode, useLazyGetPromocodeQuery } from 'app/services/promocodes';
import { Cart } from 'app/services/responseTypes';
import { TextInput } from 'Components/Auth/TextInput';
import Button from 'Components/Buttons/Button';
import Form from 'Components/Form';
import ErrorMessageStyle from 'Components/Form/ErrorMessageStyle';
import { errorToast, successToast } from 'Components/Notifications';
import Spinner from 'Components/Spinner';
import { ReactNode, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import SignEngagementLetter from './components/SignEngagementLetter';

// NEW IMPORTS FOR ADD CARD BUTTON
import { useCreateCardMutation } from 'app/services/payments/cards';
import { AddIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';

type FormInputs = {
  promocode: string;
};

const initialData: FormInputs = {
  promocode: '',
};

const validationSchema = Yup.object().shape({
  promocode: Yup.string().nullable(),
});

export function useCartCheckoutTotalPrice(cart?: Cart, promocode?: IPromoCode) {
  if (!cart)
    return {
      totalCartAmount: 0,
      discountAmount: 0,
      cartAmountWithpromoCodeApplied: 0,
    };

  const totalCartAmount =
    cart.cart_items
      .map((cart) => cart.price.amount_cents)
      .reduce((prev, curr) => prev + curr, 0) / 100;

  if (!promocode)
    return {
      totalCartAmount,
      discountAmount: 0,
      cartAmountWithpromoCodeApplied: totalCartAmount,
    };

  const discountAmount = (() => {
    const discount = promocode
      ? totalCartAmount * (promocode?.percentage / 100)
      : 0;
    return Math.round((discount + Number.EPSILON) * 100) / 100;
  })();

  const cartAmountWithpromoCodeApplied = (() => {
    const discount = promocode
      ? totalCartAmount * (promocode?.percentage / 100)
      : 0;
    const total = totalCartAmount - discount;
    return Math.round((total + Number.EPSILON) * 100) / 100;
  })();

  return {
    totalCartAmount,
    discountAmount,
    cartAmountWithpromoCodeApplied,
  };
}

export function PromoCodeForm({
  onChange,
  Cart,
}: {
  Cart?: Cart;
  onChange: (promocode?: IPromoCode) => void;
}) {
  const [getPromocode, { isLoading: isLoadingPromocode }] =
    useLazyGetPromocodeQuery();
  const [promocode, setPromocode] = useState<IPromoCode>();
  const [errorMsg, setErrorMsg] = useState<string>();

  const onCancelPromocode = () => {
    setPromocode(undefined);
    onChange(undefined);
  };

  const onApplyPromocode: SubmitHandler<FormInputs> = async ({ promocode }) => {
    promocode &&
      getPromocode(promocode)
        .unwrap()
        .then((promocode) => {
          setPromocode(promocode);
          onChange(promocode);
          setErrorMsg('');
        })
        .catch((e: any) => setErrorMsg(e?.data?.message));
  };

  return (
    <Form
      onSubmit={onApplyPromocode}
      defaultValues={initialData}
      validationSchema={validationSchema}
    >
      {({ setValue }) => (
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <TextInput
              type="text"
              name="promocode"
              placeholder="Enter Promocode"
              disable={!!promocode}
            />
            {errorMsg && <ErrorMessageStyle message={errorMsg} />}
          </div>
          <div className="w-24 h-full self-start">
            {!promocode && (
              <Button
                theme="green-outline"
                type="submit"
                disabled={isLoadingPromocode}
              >
                Apply
              </Button>
            )}
            {promocode && (
              <Button
                type="button"
                theme="red-fill"
                onClick={() => {
                  setValue('promocode', '');
                  onCancelPromocode();
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </Form>
  );
}

export function TotalOrder({
  cart,
  children,
  promocode,
}: {
  cart?: Cart;
  promocode?: IPromoCode;
  children?: ReactNode;
}) {
  const { cartAmountWithpromoCodeApplied, discountAmount, totalCartAmount } =
    useCartCheckoutTotalPrice(cart, promocode);
  return (
    <>
      <div className="p-4 border-b border-t border-gray-100">
        <div className="flex justify-between text-sm text-gray-700 ">
          <p>Total purchases</p>
          <p className="text-base">${totalCartAmount}</p>
        </div>
        {promocode && (
          <div className="flex justify-between text-sm text-gray-700 ">
            <p>Discount</p>
            <p className="text-base">-${discountAmount}</p>
          </div>
        )}
        <div className="flex justify-between text-sm text-gray-700 ">
          <p>Estimated taxes</p>
          <p className="text-base">$0</p>
        </div>
      </div>
      <div className="p-4 grid gap-2">
        <div className="p-4 flex justify-between font-bold text-gray-dark ">
          <p>Total</p>
          <p className="text-medium">${cartAmountWithpromoCodeApplied}</p>
        </div>
        {children}
      </div>
    </>
  );
}

const TaxReturnCheckout = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [hasSigned, setHasSigned] = useState(false);
  const { data, isSuccess, isLoading } = useGetProjectByIdQuery(projectId!, {
    skip: !projectId,
  });

  const {
    data: Cart,
    isLoading: isLoadingCart,
    isSuccess: isSuccessCart,
  } = useGetCartItemsQuery('Project');
  const [CreateOrder, { isLoading: isLoadingCreateOrder }] =
    useCreateOrderMutation();
  const [
    CheckoutOrder,
    { isLoading: isLoadingCheckoutOrder, isSuccess: isSuccessCheckoutOrder },
  ] = useCheckoutOrderMutation();

  const [promocode, setPromoCode] = useState<IPromoCode>();

  // NEW: Add Card mutation for the Add Card button
  const [AddCard, { isLoading: isLoadingAddCard }] = useCreateCardMutation();
  const AddCardFn = () => {
    AddCard().then((res: any) => {
      sessionStorage.setItem('shouldRedirectToCheckout', JSON.stringify(true));
      window.location.replace(res?.data?.redirect_url);
    });
  };

  const { cartAmountWithpromoCodeApplied, discountAmount, totalCartAmount } =
    useCartCheckoutTotalPrice(Cart, promocode);

  // The Pay function: identical to clicking the Pay button at the bottom.
  const Pay = () => {
    const CardItemsIds = Cart?.cart_items.map(
      (cart: { id: number }) => cart.id,
    );
    CreateOrder({
      order: {
        promo_code: promocode?.code,
        cart_items_ids: CardItemsIds!,
      },
    })
      .unwrap()
      .then(({ order }) => {
        CheckoutOrder(order.id)
          .unwrap()
          .then((res) => {
            if (res && res.redirect_url) {
              window.location.replace(res?.redirect_url);
            } else if (order.total_price === 0) {
              successToast('Your order is now completed');
              navigate('/dashboard/orders');
            }
          })
          .catch((e) => {
            errorToast('Please Try Again');
          });
      })
      .catch((e) => {
        errorToast(e.data.message);
      });
  };

  useEffect(() => {
    if (data) {
      if (data.project.price.amount_cents === 0) {
        navigate('/dashboard/questionnaires');
      }
    }
  }, [data]);

  useEffect(() => {
    if (window.location.href.includes('/checkout?event=signing_complete')) {
      setHasSigned(true);
    }
  }, []);

  if (isLoading || !data) return <Spinner />;
  const { engagement_letter } = data.project;
  const isEngagementLetterRequired = engagement_letter !== undefined;

  return (
    <ScrollContainer>
      {/* Modified header with the Add Card and Go To Pay buttons beside the title */}
      <DashboardContainerHeader title="Checkout">
        <div className="flex items-center gap-2">
          <div className="max-w-min whitespace-nowrap">
            {isLoadingAddCard ? (
              <Spinner bounce={false} />
            ) : (
              <CustomButton
                text="Add Card"
                height="10"
                prefix={<AddIcon />}
                textSize="text-sm"
                bgColor="white"
                borderColor="green"
                textColor="green"
                onClick={AddCardFn}
              />
            )}
          </div>
          <div className="max-w-min whitespace-nowrap">
            <CustomButton
              text="Go to Pay"
              height="10"
              textSize="text-sm"
              onClick={Pay}
            />
          </div>
        </div>
      </DashboardContainerHeader>
      <div className="overflow-y-auto grid grid-cols-[3fr,2fr] md-max:w-full h-full md-max:grid-cols-1">
        <div className="h-full w-full border-r border-gray-100 md-max:border-0 md-max:gap-2">
          {!hasSigned &&
            isEngagementLetterRequired &&
            engagement_letter.signing_status === 'pending' && (
              <div className="mb-6 mt-2">
                <SignEngagementLetter sign_url={engagement_letter.sign_url} />
              </div>
            )}
          <PaymentCards />
        </div>
        <ScrollContainer className="h-full">
          <DashboardContainerHeader title="Your Payment" />
          {(isLoadingCheckoutOrder ||
            isLoadingCheckoutOrder ||
            isLoadingCart) && <Spinner />}
          {Cart && <CartItemCard Cart={Cart} type="Project" />}
          <div className="mt-8 mb-4">
            <div className="p-4 border-t border-gray-100">
              <PromoCodeForm Cart={Cart} onChange={setPromoCode} />
            </div>
            <TotalOrder cart={Cart} promocode={promocode}>
              <Button
                theme="green-fill"
                className="py-4"
                disabled={
                  (!hasSigned &&
                    isEngagementLetterRequired &&
                    engagement_letter.signing_status === 'pending') ||
                  totalCartAmount === 0
                }
                onClick={Pay}
                isLoading={
                  isLoadingCheckoutOrder ||
                  isLoadingCreateOrder ||
                  isSuccessCheckoutOrder
                }
              >
                Pay
              </Button>
            </TotalOrder>
          </div>
        </ScrollContainer>
      </div>
    </ScrollContainer>
  );
};

export default TaxReturnCheckout;
